"use client";

import type { FC } from "react";
import { useRouter } from "next/navigation";
import { cva, type VariantProps } from "class-variance-authority";
import { useInquiry } from "@/features/inquiries";
import { useHasDeviceLocation } from "@/features/maps";
import { useSavedPractitioners } from "@/features/saved-practitioners";
import { cn } from "@/lib/cn";
import { AdminLayout } from "./admin-layout";
import { CompactLayout } from "./compact-layout";
import { toCardContent } from "./card-content";
import { DirectoryLayout } from "./directory-layout";
import { MapSideBarLayout } from "./map-side-bar-layout";
import { MobileLayout } from "./mobile-layout";
import type { PractitionerCardSource } from "./source";
import { StickyLayout } from "./sticky-layout";
import { usePrefetchOnHover } from "./use-prefetch-on-hover";

const practitionerCardVariants = cva("", {
  variants: {
    variant: {
      directory: "overflow-hidden bg-neutral-white w-full rounded-large max-lg:border max-lg:border-neutral-light-grey",
      /* Radius matches the InfoWindow bubble's 24px (globals.css .gm-style-iw-c) */
      "map-hover-card": "overflow-hidden bg-neutral-white w-[min(301px,calc(100vw_-_48px))] rounded-large",
      /* Transparent border keeps the size stable when the active card's border turns brand-blue */
      "map-carousel": "overflow-hidden bg-neutral-white w-full rounded-large border-[1.5px] border-transparent",
      "map-side-bar": "overflow-hidden bg-neutral-white w-full rounded-large",
      sticky: "relative flex flex-col items-center gap-24 rounded-large bg-neutral-white p-24 text-center",
      mobile:
        "relative flex flex-col items-center gap-16 rounded-large border-2 border-brand-dark-blue bg-neutral-white p-16 text-center",
      admin: "flex flex-col items-center gap-24 rounded-large bg-background-grey p-24 text-center",
    },
  },
  defaultVariants: {
    variant: "directory",
  },
});

export type PractitionerCardVariant = VariantProps<typeof practitionerCardVariants>["variant"];

type PractitionerCardProps = VariantProps<typeof practitionerCardVariants> & {
  source?: PractitionerCardSource;
  onShare?: () => void;
  loading?: boolean;
  expanded?: boolean;
  className?: string;
};

const CLICKABLE_VARIANTS: PractitionerCardVariant[] = [undefined, "directory", "map-hover-card"];

export const PractitionerCard: FC<PractitionerCardProps> = ({
  source,
  onShare,
  loading,
  variant,
  expanded,
  className,
}) => {
  const router = useRouter();
  const { contact } = useInquiry();
  const { isSaved, toggle } = useSavedPractitioners();
  const hasDeviceLocation = useHasDeviceLocation();

  const content = source ? toCardContent(source, hasDeviceLocation) : undefined;
  const username = source?.username;
  const favorited = username ? isSaved(username) : false;
  const onFavoriteToggle = username ? () => toggle(username) : undefined;
  const onContact = username && content ? () => contact(username, content.name) : undefined;

  const layout = {
    content,
    favorited,
    onContact,
    onFavoriteToggle,
    onShare,
    loading: Boolean(loading) || !content,
    expanded,
  };

  const profileHref = content?.profileHref;
  const clickable = Boolean(profileHref) && CLICKABLE_VARIANTS.includes(variant);
  const onCardClick = clickable && profileHref ? () => router.push(profileHref) : undefined;
  const hoverPrefetch = usePrefetchOnHover(clickable ? profileHref : undefined);

  return (
    <div
      role={layout.loading ? "status" : undefined}
      aria-label={layout.loading ? "Loading practitioner" : undefined}
      onClick={onCardClick}
      {...hoverPrefetch}
      className={cn(
        practitionerCardVariants({ variant }),
        clickable && "relative cursor-pointer transition-shadow duration-150 hover:shadow-md",
        className,
      )}
    >
      {variant === "map-hover-card" && <CompactLayout {...layout} expanded />}
      {variant === "map-carousel" && <CompactLayout {...layout} />}
      {variant === "map-side-bar" && <MapSideBarLayout {...layout} />}
      {variant === "sticky" && <StickyLayout {...layout} />}
      {variant === "mobile" && <MobileLayout {...layout} />}
      {variant === "admin" && <AdminLayout {...layout} />}
      {(variant === "directory" || variant === undefined) && <DirectoryLayout {...layout} />}
    </div>
  );
};
